<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <!-- Show message when no ID -->
          <v-card v-if="!hasValidId" elevation="1">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-information-outline</v-icon>
              <div class="text-h6 mb-2">No Process Selected</div>
              <div class="text-body-2 text-medium-emphasis mb-4">
                Please select a salary process from the list to view details.
              </div>
              <v-btn
                color="primary"
                variant="tonal"
                @click="router.push({ name: 'salary-process-list' })"
              >
                <v-icon start>mdi-format-list-bulleted</v-icon>
                Go to Salary Process List
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Show loading state -->
          <v-card v-else-if="loading" elevation="1">
            <v-card-text class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              />
              <div class="text-body-1 mt-4">Loading process details...</div>
            </v-card-text>
          </v-card>

          <!-- Show error state -->
          <v-card v-else-if="error" elevation="1">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
              <div class="text-h6 mb-2 text-error">Error Loading Details</div>
              <div class="text-body-2 text-medium-emphasis mb-4">{{ error }}</div>
              <v-btn
                color="primary"
                variant="tonal"
                @click="fetchProcessDetails"
                class="mr-2"
              >
                Retry
              </v-btn>
              <v-btn
                color="grey"
                variant="text"
                @click="router.push({ name: 'salary-process-list' })"
              >
                Back to List
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Show process details -->
          <v-card v-else-if="processData" elevation="1">
            <v-card-title class="text-h5 pa-4">
              <v-icon class="me-2">mdi-account-details</v-icon>
              Salary Process Details - {{ processData.code }}
              <v-progress-circular v-if="loading" indeterminate size="20" class="ml-2" color="primary"></v-progress-circular>
              <v-spacer></v-spacer>
              <v-chip
                :color="getStatusColor(processData.salary_status)"
                size="small"
                class="text-capitalize"
              >
                {{ formatStatusLabel(processData.salary_status) }}
              </v-chip>
            </v-card-title>

            <v-card-text class="pa-4">
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Process Code</div>
                  <div class="text-body-1 font-weight-medium">{{ processData.code }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Process Date</div>
                  <div class="text-body-1">{{ formatDateView(processData.process_date) }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Year / Month</div>
                  <div class="text-body-1">{{ processData.year }} / {{ getMonthName(processData.month) }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Process Scope</div>
                  <v-chip
                    :color="getScopeColor(processData.process_scope)"
                    size="small"
                    label
                  >
                    {{ formatScopeLabel(processData.process_scope) }}
                  </v-chip>
                </v-col>
                <v-col cols="12" md="3" v-if="processData.department">
                  <div class="text-caption text-medium-emphasis">Department</div>
                  <div class="text-body-1">{{ processData.department?.name || '—' }}</div>
                </v-col>
                <v-col cols="12" md="3" v-if="processData.employee">
                  <div class="text-caption text-medium-emphasis">Employee</div>
                  <div class="text-body-1">{{ processData.employee?.name || '—' }}</div>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <v-row class="mb-3">
                <v-col cols="12" md="6">
                  <div class="text-h6">Employee Salary Details</div>
                </v-col>
                <v-col cols="12" md="6" class="d-flex justify-end">
                  <v-btn-group
                    v-if="processData?.details && processData.details.length > 0"
                    variant="outlined"
                    density="compact"
                    size="x-small"
                    divided
                  >
                    <v-btn
                      prepend-icon="mdi mdi-download"
                      variant="flat"
                      color="success"
                      @click="exportToCSV"
                      :disabled="!processData?.details?.length"
                    >
                      CSV
                    </v-btn>
                    <v-btn
                      prepend-icon="mdi mdi-download"
                      variant="flat"
                      color="primary"
                      @click="exportToExcel"
                      :disabled="!processData?.details?.length"
                    >
                      EXCEL
                    </v-btn>
                    <v-btn
                      prepend-icon="mdi mdi-download"
                      variant="flat"
                      color="red"
                      @click="exportToPDF"
                      :disabled="!processData?.details?.length"
                    >
                      PDF
                    </v-btn>
                  </v-btn-group>
                </v-col>
              </v-row>
              <v-data-table
                :headers="detailsTableHeaders"
                :items="processData?.details || []"
                :loading="loading"
                density="compact"
                class="elevation-1"
              >
                <template #item.index="{ index }">
                  {{ index + 1 }}
                </template>
                <template #item.employee_code="{ item }">
                  {{ item.employee?.code || '—' }}
                </template>
                <template #item.employee_name="{ item }">
                  {{ item.employee?.name || '—' }}
                </template>
                <template #item.gross_payable="{ item }">
                  {{ formatNumber(item.gross_payable) }}
                </template>
                <template #item.total_deduction="{ item }">
                  {{ formatNumber(item.total_deduction) }}
                </template>
                <template #item.total_net_payable="{ item }">
                  {{ formatNumber(item.total_net_payable) }}
                </template>
                <template #body.append v-if="processData?.details && processData.details.length > 0">
                  <tr style="font-weight: bold; background-color: #f5f5f5;">
                    <td class="text-center">—</td>
                    <td colspan="2" class="text-end" style="padding: 12px;">
                      <strong>Total:</strong>
                    </td>
                    <td class="text-end" style="padding: 12px;">
                      <strong>{{ formatNumber(totals.gross_payable) }}</strong>
                    </td>
                    <td class="text-end" style="padding: 12px;">
                      <strong>{{ formatNumber(totals.total_deduction) }}</strong>
                    </td>
                    <td class="text-end" style="padding: 12px;">
                      <strong>{{ formatNumber(totals.total_net_payable) }}</strong>
                    </td>
                  </tr>
                </template>
                <template #loading>
                  <div class="py-6 text-center text-medium-emphasis">
                    Loading employee details...
                  </div>
                </template>
                <template #no-data>
                  <div class="py-6 text-center text-medium-emphasis">
                    No employee details found.
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn color="primary" variant="outlined" @click="router.push({ name: 'salary-process-list' })">
                <v-icon class="me-2">mdi-arrow-left</v-icon>
                Back to List
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, watch, computed, getCurrentInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import { formatDateView, formatDateDash } from "@/plugins/helpers";

const instance = getCurrentInstance()
const appContext = instance?.appContext
const store = useStore()

const route = useRoute()
const router = useRouter()
const loading = ref(false)
const processData = ref(null)
const error = ref(null)

// Computed to check if we have a valid ID
const hasValidId = computed(() => {
  const id = route.params.id
  return id && id !== 'undefined' && id !== null && id !== ''
})

const detailsTableHeaders = [
  { title: '#SL', key: 'index', sortable: false, width: '60px', align: 'center' },
  { title: 'Employee Code', key: 'employee_code', sortable: false },
  { title: 'Employee Name', key: 'employee_name', sortable: false },
  { title: 'Gross Payable', key: 'gross_payable', sortable: false, align: 'end' },
  { title: 'Total Deduction', key: 'total_deduction', sortable: false, align: 'end' },
  { title: 'Total Net Payable', key: 'total_net_payable', sortable: false, align: 'end' }
]

const getMonthName = (monthNumber) => {
  if (!monthNumber) return '—'
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return monthNames[monthNumber - 1] || monthNumber
}

const formatScopeLabel = (scope) => {
  switch (scope) {
    case 'department':
      return 'Department'
    case 'employee':
      return 'Employee'
    default:
      return 'All'
  }
}

const getScopeColor = (scope) => {
  switch (scope) {
    case 'department':
      return 'deep-purple-lighten-2'
    case 'employee':
      return 'teal-lighten-2'
    default:
      return 'blue-grey-lighten-2'
  }
}

const formatStatusLabel = (status) => {
  if (!status) return '—'
  return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')
}

const getStatusColor = (status) => {
  if (!status) return 'grey'
  switch (status.toLowerCase()) {
    case 'pending':
      return 'orange'
    case 'approved':
    case 'completed':
    case 'processed':
      return 'success'
    case 'rejected':
    case 'cancelled':
      return 'error'
    case 'processing':
    case 'partially_paid':
      return 'info'
    case 'paid':
      return 'success'
    default:
      return 'grey'
  }
}

const fetchProcessDetails = async () => {
  const id = route.params.id
  if (!id || id === 'undefined' || id === null || id === '') {
    error.value = 'Process ID is required'
    return
  }

  // Convert ID to number if it's a string
  const processId = typeof id === 'string' ? parseInt(id, 10) : id
  if (isNaN(processId)) {
    error.value = 'Invalid Process ID'
    return
  }

  loading.value = true
  error.value = null
  try {
    console.log('Fetching process details for ID:', processId)
    const response = await store.dispatch('salaryProcess/getSalaryProcessById', processId)
    console.log('Process details response:', response)
    if (response.success) {
      processData.value = response.data
      console.log('Process data loaded:', processData.value)
      console.log('Details count:', processData.value?.details?.length || 0)
    } else {
      error.value = response.message || 'Failed to load process details'
      toast.error(error.value)
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message || 'Failed to load process details'
    console.error('Error loading process details:', err)
    toast.error(error.value)
  } finally {
    loading.value = false
  }
}

// Watch for route parameter changes
watch(() => route.params.id, (newId) => {
  if (newId && newId !== 'undefined') {
    fetchProcessDetails()
  } else {
    processData.value = null
    error.value = null
  }
}, { immediate: true })

// Computed for company info
const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})

// Format number for export
const formatNumber = (value, fraction = 2) => {
  if (value === undefined || value === null || value === '' || isNaN(value)) return '0.00'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

// Computed totals
const totals = computed(() => {
  if (!processData.value?.details) {
    return {
      gross_payable: 0,
      total_deduction: 0,
      total_net_payable: 0
    }
  }
  return processData.value.details.reduce(
    (acc, item) => {
      acc.gross_payable += Number(item.gross_payable) || 0
      acc.total_deduction += Number(item.total_deduction) || 0
      acc.total_net_payable += Number(item.total_net_payable) || 0
      return acc
    },
    {
      gross_payable: 0,
      total_deduction: 0,
      total_net_payable: 0
    }
  )
})

// Export to Excel
const exportToExcel = () => {
  if (!processData.value || !processData.value.details || processData.value.details.length === 0) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Salary Process Details'],
    [`Process Code: ${processData.value.code}`],
    [`Process Date: ${formatDateView(processData.value.process_date)}`],
    [`Year / Month: ${processData.value.year} / ${getMonthName(processData.value.month)}`],
    [`Process Scope: ${formatScopeLabel(processData.value.process_scope)}`],
    [`Status: ${formatStatusLabel(processData.value.salary_status)}`]
  ]
  
  if (processData.value.department) {
    data.push([`Department: ${processData.value.department?.name || '—'}`])
  }
  if (processData.value.employee) {
    data.push([`Employee: ${processData.value.employee?.name || '—'}`])
  }
  
  data.push([], ['# SL', 'Employee Code', 'Employee Name', 'Gross Payable', 'Total Deduction', 'Total Net Payable'], [])

  // Add data rows
  processData.value.details.forEach((item, index) => {
    data.push([
      index + 1,
      item.employee?.code || '—',
      item.employee?.name || '—',
      formatNumber(item.gross_payable),
      formatNumber(item.total_deduction),
      formatNumber(item.total_net_payable)
    ])
  })

  // Add totals row
  if (processData.value.details.length > 0) {
    data.push([])
    data.push([
      '', '', 'Total:',
      formatNumber(totals.value.gross_payable),
      formatNumber(totals.value.total_deduction),
      formatNumber(totals.value.total_net_payable)
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'salary_process_details_' + processData.value.code + '_' + proxy.$fileDate())
  }
}

// Export to PDF
const exportToPDF = async () => {
  if (!processData.value || !processData.value.details || processData.value.details.length === 0) {
    toast.warning('No data to export')
    return
  }

  const company = getCurrentCompany.value
  const fileServerUrl = getFileServerUrl.value

  let printWindow = window.open('', 'PRINT', `width=${screen.width}, height=${screen.height}, left=0, top=0`)
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta http-equiv="X-UA-Compatible" content="ie=edge">
        <title>Salary Process Details</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
        <style>
          body { font-family: 'Arimo', sans-serif; margin: 0px; }
          .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
          .invoice-header img { max-height: 70px; max-width: 150px; }
          .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 12px; }
          .table th { background-color: #f0f0f0; color: #333; }
          .text-end { text-align: right; }
          .text-center { text-align: center; }
          h4 { font-size: 20px; color: #333; }
          p { font-size: 14px; color: #555; margin: 2px 0; }
          strong { font-weight: bold; color: #333; }
          @page { margin: 0.5in; size: letter; }
          .logo-container { display:flex; align-items:center; }
          .logo-container img { margin-left: 10px; }
          .table { width: 100%; border-collapse: collapse; table-layout: auto; }
          thead { display: table-header-group; }
          .totals-row { page-break-inside: avoid; }
          .totals-row td { font-weight: bold; background-color: #f5f5f5 !important; }
        </style>
      </head>
      <body>
        <div class="container-fluid position-relative">
          <div class="invoice-header">
            <div>
              <h4 class="text-uppercase">${company?.name || 'Company Name'}</h4>
              <p style="width:65%">${company?.address || ''}</p>
              <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ''}</p>
              <p><i class="mdi mdi-email-outline"></i> ${company?.email || ''}</p>
              ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ''}
            </div>
            <div class="logo-container">
              ${company?.logo_thumb ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">` : ''}
            </div>
          </div>
          <h4 class="text-center my-4"><strong>SALARY PROCESS DETAILS</strong></h4>
          <div class="row mb-3">
            <div class="col-sm-8">
              <p><strong>Process Code:</strong> ${processData.value.code}</p>
              <p><strong>Process Date:</strong> ${formatDateView(processData.value.process_date)}</p>
              <p><strong>Year / Month:</strong> ${processData.value.year} / ${getMonthName(processData.value.month)}</p>
              <p><strong>Process Scope:</strong> ${formatScopeLabel(processData.value.process_scope)}</p>
              <p><strong>Status:</strong> ${formatStatusLabel(processData.value.salary_status)}</p>
              ${processData.value.department ? `<p><strong>Department:</strong> ${processData.value.department?.name || '—'}</p>` : ''}
              ${processData.value.employee ? `<p><strong>Employee:</strong> ${processData.value.employee?.name || '—'}</p>` : ''}
            </div>
            <div class="col-sm-4 text-end">
              <p><strong>Total Records:</strong> ${processData.value.details.length}</p>
            </div>
          </div>
          <table class="table mt-4">
            <thead>
              <tr>
                <th>SN</th>
                <th>Employee Code</th>
                <th>Employee Name</th>
                <th class="text-end">Gross Payable</th>
                <th class="text-end">Total Deduction</th>
                <th class="text-end">Total Net Payable</th>
              </tr>
            </thead>
            <tbody>
              ${processData.value.details.map((item, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${item.employee?.code || '—'}</td>
                  <td><strong>${item.employee?.name || '—'}</strong></td>
                  <td class="text-end">${formatNumber(item.gross_payable)}</td>
                  <td class="text-end">${formatNumber(item.total_deduction)}</td>
                  <td class="text-end">${formatNumber(item.total_net_payable)}</td>
                </tr>
              `).join('')}
              <tr class="totals-row">
                <td colspan="3" class="text-end"><strong>Total:</strong></td>
                <td class="text-end"><strong>${formatNumber(totals.value.gross_payable)}</strong></td>
                <td class="text-end"><strong>${formatNumber(totals.value.total_deduction)}</strong></td>
                <td class="text-end"><strong>${formatNumber(totals.value.total_net_payable)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </body>
    </html>
  `)
  printWindow.moveTo(0, 0)
  printWindow.focus()
  await new Promise(resolve => setTimeout(resolve, 500))
  printWindow.print()
  printWindow.close()
}

// Export to CSV
const exportToCSV = () => {
  if (!processData.value || !processData.value.details || processData.value.details.length === 0) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Salary Process Details'],
    [`Process Code: ${processData.value.code}`],
    [`Process Date: ${formatDateView(processData.value.process_date)}`],
    [`Year / Month: ${processData.value.year} / ${getMonthName(processData.value.month)}`],
    [`Process Scope: ${formatScopeLabel(processData.value.process_scope)}`],
    [`Status: ${formatStatusLabel(processData.value.salary_status)}`]
  ]
  
  if (processData.value.department) {
    data.push([`Department: ${processData.value.department?.name || '—'}`])
  }
  if (processData.value.employee) {
    data.push([`Employee: ${processData.value.employee?.name || '—'}`])
  }
  
  data.push([], ['# SL', 'Employee Code', 'Employee Name', 'Gross Payable', 'Total Deduction', 'Total Net Payable'], [])

  // Add data rows
  processData.value.details.forEach((item, index) => {
    data.push([
      index + 1,
      item.employee?.code || '—',
      item.employee?.name || '—',
      formatNumber(item.gross_payable),
      formatNumber(item.total_deduction),
      formatNumber(item.total_net_payable)
    ])
  })

  // Add totals row
  if (processData.value.details.length > 0) {
    data.push([])
    data.push([
      '', '', 'Total:',
      formatNumber(totals.value.gross_payable),
      formatNumber(totals.value.total_deduction),
      formatNumber(totals.value.total_net_payable)
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToCSV && proxy?.$fileDate) {
    proxy.$exportTableToCSV(data, 'salary_process_details_' + processData.value.code + '_' + proxy.$fileDate())
  }
}
</script>

<style scoped>
</style>

